import { Snackbar, Tab, Tabs } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Alert, AlertTitle } from "@material-ui/lab";
import { styled, withStyles } from "@material-ui/styles";
import { Collection } from "@type-r/models";
import { useCollection, useIO, useLinked, useModel } from "@type-r/react";
import { orderBy } from "lodash";
import { Column, Columns, Flex, Loading, div, navigate, span } from "lqx-react";
import React, { useEffect, useState } from "react";
import { Cell } from "react-datasheet/lib";
import * as XLSX from "xlsx";
import { AssetFilterCriteria, WorkSheet } from "../../api/assetFilterCriteria";
import { AssetTypeItem } from "../../api/assetTypes";
import { ImportTemplateCreate } from "../../api/importTemplateCreate";
import { ImportTemplateUpdate } from "../../api/importTemplateUpdate";
import { TargetFieldCard } from "../../api/targetFieldCard";
import { DigiSelect, DigitizationSelect } from "../../components/select";
import { BackButton, CancelButton, ConfirmationButton, DigiCheckbox, RightAlignedButtons, StartOverButton, WorkflowFooter } from "../../shared";
import { FileDownloadButton } from "../common/file-download-button";
import { ComputedFieldItem, Formatting, ImportTemplateDetails, Mapping, MappingItem, StaticFieldItem } from "../models/import-template-details";
import { importTemplate } from "../models/import-wizard-constants";
import { FileMapperFields } from "./import-template-mapper/file-mapper-fields";
import { FileReaderView } from "./import-template-mapper/file-reader-view";
import { getValidationSets } from "../../api/validationRuleItem";
import { ValidationRuleDetails } from "../../api/validationRuleDetails";

interface IinBlockMappingField {
    assetTypeId: number,
    mappingField: TargetFieldCard[]
}

export const ImportTemplateMapperStep = ({ handleState, back, cancel, startOver, importTemplateDetails, goToStep, showSuccessfulEditMessage }
    : { handleState?: any, back?: any, cancel?: any, startOver?: any, importTemplateDetails?: ImportTemplateDetails, goToStep?, showSuccessfulEditMessage?: any }) => {

    const targetFieldCards = useCollection.of(TargetFieldCard);
    const isEdit = importTemplateDetails.id ? true : false;
    const isStandard = importTemplateDetails.isStandard;
    const $selectColumnMode = useLinked<boolean>(false);
    const $omitUnmappedFields = useLinked(importTemplateDetails.omitUnmappedFields);
    const $currentAssetType = useLinked<AssetTypeItem>(importTemplateDetails.assetTypes[0]);
    const $isLoading = useLinked<boolean>(false);
    const $importTemplatesMappingItems = useLinked<MappingItem[]>([]);
    const $importTemplatesStaticFieldItems = useLinked<StaticFieldItem[]>([]);
    const $importTemplatesComputedFieldItems = useLinked<ComputedFieldItem[]>([]);
    const $sheetList = useLinked<any[]>([]);
    const $workSheet = useLinked<WorkSheet>(importTemplateDetails.workSheet);
    const classes = useTextStyles();
    const importTemplateCreate = useModel(ImportTemplateCreate);
    const importTemplateUpdate = useModel(ImportTemplateUpdate);
    const $showErrors = useLinked(false);
    const $errorMessage = useLinked("Error happened!");
    const $disableSave = useLinked(false);
    const validationRuleDetails = useModel(ValidationRuleDetails);

    const isPlainTextFile = (): boolean => {
        if (!importTemplateDetails.file) return false;

        let validPlainTextExtensions = ['csv', 'txt'];
        const extension = importTemplateDetails.file.name.split('.').pop();

        return validPlainTextExtensions.includes(extension);
    }

    const [state, setState] = useState<{
        data: Cell<string, string>[][],
        fileBinary: ArrayBuffer,
        inBlockMappingFields: IinBlockMappingField[],
        omitUnmappedFields: Boolean
    }>()

    const getData = async (id) => await targetFieldCards.fetch({ params: { assetTypeId: id } });

    useEffect(() => {
        if (!importTemplateDetails.mappings.some(i => i.assetTypeId == +$currentAssetType.value.id)) {
            handleState({
                mappings: [...importTemplateDetails.mappings, { assetTypeId: $currentAssetType.value.id, mappingItems: [] }]
            });
            $importTemplatesMappingItems.set([]);
        } else {
            $importTemplatesMappingItems.set(importTemplateDetails.mappings.filter(i => i.assetTypeId == +$currentAssetType.value.id)[0].mappingItems);
        }

        if (!importTemplateDetails.staticFields.some(i => i.assetTypeId == +$currentAssetType.value.id)) {
            handleState({
                staticFields: [...importTemplateDetails.staticFields, { assetTypeId: $currentAssetType.value.id, staticFieldItems: [] }]
            });

            $importTemplatesStaticFieldItems.set([]);
        } else {
            $importTemplatesStaticFieldItems.set(importTemplateDetails.staticFields.filter(i => i.assetTypeId == +$currentAssetType.value.id)[0].staticFieldItems);
        }

        if (!importTemplateDetails.computedFields.some(i => i.assetTypeId == +$currentAssetType.value.id)) {
            handleState({
                computedFields: [...importTemplateDetails.computedFields, { assetTypeId: $currentAssetType.value.id, computedFieldItems: [] }]
            });

            $importTemplatesComputedFieldItems.set([]);
        } else {
            $importTemplatesComputedFieldItems.set(importTemplateDetails.computedFields.filter(i => i.assetTypeId == +$currentAssetType.value.id)[0].computedFieldItems);
        }
    }, [$currentAssetType.value]);

    useEffect(() => {
        handleState({
            mappings: [...importTemplateDetails.mappings.map(i => {
                if (i.assetTypeId == +$currentAssetType.value.id) {
                    return ({ assetTypeId: i.assetTypeId, mappingItems: [...$importTemplatesMappingItems.value], validationRuleId: i.validationRuleId });
                }

                return i;
            })]
        })

        if (!importTemplateDetails.mappings.some(i => i.assetTypeId == +$currentAssetType.value.id)) {
            handleState({ mappings: [...importTemplateDetails.mappings, { assetTypeId: $currentAssetType.value.id, mappingItems: [...$importTemplatesMappingItems.value] }] });
        }

    }, [$importTemplatesMappingItems.value]);

    useEffect(() => {
        handleState({
            staticFields: [...importTemplateDetails.staticFields.map(i => {
                if (i.assetTypeId == +$currentAssetType.value.id) {
                    return ({ assetTypeId: i.assetTypeId, staticFieldItems: [...$importTemplatesStaticFieldItems.value] });
                }
                return i;
            })]
        })

        if (!importTemplateDetails.staticFields.some(i => i.assetTypeId == +$currentAssetType.value.id)) {
            handleState({ staticFields: [...importTemplateDetails.staticFields, { assetTypeId: $currentAssetType.value.id, staticFieldItems: [...$importTemplatesStaticFieldItems.value] }] })
        }

    }, [$importTemplatesStaticFieldItems.value]);

    useEffect(() => {
        handleState({
            computedFields: [...importTemplateDetails.computedFields.map(i => {
                if (i.assetTypeId == +$currentAssetType.value.id) {
                    return ({ assetTypeId: i.assetTypeId, computedFieldItems: [...$importTemplatesComputedFieldItems.value] });
                }
                return i;
            })]
        })

        if (!importTemplateDetails.computedFields.some(i => i.assetTypeId == +$currentAssetType.value.id)) {
            handleState({ computedFields: [...importTemplateDetails.computedFields, { assetTypeId: $currentAssetType.value.id, computedFieldItems: [...$importTemplatesComputedFieldItems.value] }] })
        }

    }, [$importTemplatesComputedFieldItems.value]);

    const isReady = useIO(async () => {
        const fields = (await getData($currentAssetType.value.id).catch(e => console.error(e))) as Collection<TargetFieldCard>;
        if (importTemplateDetails.mappings.length > 0
            && importTemplateDetails.mappings.some(i => i.assetTypeId == +$currentAssetType.value.id)
            && importTemplateDetails.mappings.find(i => i.assetTypeId == +$currentAssetType.value.id).assetTypeId == +$currentAssetType.value.id
            && importTemplateDetails.mappings.find(i => i.assetTypeId == +$currentAssetType.value.id).validationRuleId) { //TODO support multiple asset types
            await validationRuleDetails.get(importTemplateDetails.mappings.find(i => i.assetTypeId == +$currentAssetType.value.id).validationRuleId.toString()).then();
            $validationSets.set({ id: validationRuleDetails.id, name: validationRuleDetails.name });
        } else {
            await validationRuleDetails.getStandard($currentAssetType.value.id).then();
            $validationSets.set({ id: validationRuleDetails.id, name: validationRuleDetails.name });
        }
        const defaultOption = {
            id: importTemplate.selectOption.id,
            name: importTemplate.selectOption.name,
            description: importTemplate.selectOption.description,
            assetTypeId: 1,
            isRequired: true,
            isActive: true
        } as TargetFieldCard;

        if (isStandard) {
            setState(prev => ({
                ...prev,
                inBlockMappingFields: prev ? [...prev.inBlockMappingFields, {
                    assetTypeId: +$currentAssetType.value.id,
                    mappingField: [defaultOption, ...fields.models]
                }] : [
                    {
                        assetTypeId: +$currentAssetType.value.id,
                        mappingField: [defaultOption, ...fields.models]
                    }
                ],
            }));

        } else {
            const bin = await (importTemplateDetails.file as any).arrayBuffer();

            setState(prev => ({
                ...prev,
                data: readWorkSheet(bin),
                inBlockMappingFields: prev ? [...prev.inBlockMappingFields, {
                    assetTypeId: +$currentAssetType.value.id,
                    mappingField: [defaultOption, ...fields.models]
                }] : [
                    {
                        assetTypeId: +$currentAssetType.value.id,
                        mappingField: [defaultOption, ...fields.models]
                    }
                ],
                fileBinary: bin
            }));

            useEffect(() => {
                if (isReady) {
                    setState({
                        ...state,
                        data: readWorkSheet(state.fileBinary)
                    })
                }
            }, [importTemplateDetails.mappings]);

            useEffect(() => {
                if (isReady) {
                    setState({
                        ...state,
                        omitUnmappedFields: $omitUnmappedFields.value
                    })
                }
            }, [$omitUnmappedFields.value]);
        }
    }, []);

    useEffect(() => {
        if (isReady && $workSheet && ($workSheet.value.id !== null || $workSheet.value.id !== undefined)) {
            setState({ ...state, data: readWorkSheet(state.fileBinary) });
            handleState({ workSheet: $workSheet.value });
        }
    }, [$workSheet['_changeToken']]);

    useEffect(() => {
        const defaultOption = {
            id: importTemplate.selectOption.id,
            name: importTemplate.selectOption.name,
            description: importTemplate.selectOption.description,
            assetTypeId: 1,
            isRequired: true,
            isActive: true
        } as TargetFieldCard;

        const fetchData = async () => {
            $isLoading.set(true);
            const fields = (await getData($currentAssetType.value.id).catch(e => console.error(e))) as Collection<TargetFieldCard>;
            setState(prev => ({
                ...prev,
                inBlockMappingFields: [...prev.inBlockMappingFields, {
                    assetTypeId: +$currentAssetType.value.id,
                    mappingField: [defaultOption, ...fields.models]
                }],
            }));
            $isLoading.set(false);
        }
        if (state && !state.inBlockMappingFields.some(i => i.assetTypeId == +$currentAssetType.value.id)) {
            fetchData();
        }

    }, [$currentAssetType.value]);

    const currentInBlockMappingFieldsId = () => state.inBlockMappingFields.findIndex(i => i.assetTypeId === +$currentAssetType.value.id) >= 0 ? state.inBlockMappingFields.findIndex(i => i.assetTypeId === +$currentAssetType.value.id) : 0;

    const readWorkSheet = (fileBinary) => {
        const defaultRowRange = 100;
        const wb = XLSX.read(fileBinary, {
            type: 'binary',
            sheetRows: defaultRowRange,
            cellDates: isPlainTextFile() ? false : true,
            raw: isPlainTextFile() ? true : false,
            FS: importTemplateDetails.delimiter
        });
        if (!$sheetList.value.length && wb.SheetNames.length > 1) {
            wb.SheetNames.map((s, i) => $sheetList.value.push({ id: i, name: s }));
            if (!isEdit) {
                $workSheet.set($sheetList.value[0]);
                handleState({ workSheet: $sheetList.value[0] });
            }
        };
        const wsName = wb.SheetNames[$workSheet.value.id];
        const ws = wb.Sheets[wsName];
        return XLSX.utils.sheet_to_json<string[]>(ws, { header: 1, raw: isPlainTextFile() ? true : false, defval: '' });
    };

    const onDelete = (fromName: string, isChild: boolean, index: number) => {
        var filteredMappings = $importTemplatesMappingItems.value.filter((item) => !(item.index == index || (item.from.name == fromName && !isChild)));
        if (!filteredMappings.length) {
            $omitUnmappedFields.set(false);
        }
        //remap to align index
        filteredMappings = filteredMappings.map((mappingItem, index) => ({
            from: {
                name: mappingItem.from.name, x: mappingItem.from.x, y: mappingItem.from.y, isGlobal: mappingItem.from.isGlobal, globalValuePosition: mappingItem.from.globalValuePosition, formatting: mappingItem.from.formatting ?
                    { name: mappingItem.from.formatting.name, value: mappingItem.from.formatting.value } : null
            },
            to: { id: mappingItem.to.id.toString(), name: mappingItem.to.name, targetFieldTypeId: mappingItem.to.targetFieldTypeId },
            isChild: mappingItem.isChild ? mappingItem.isChild : false,
            index: index + 1
        }))

        $importTemplatesMappingItems.set(filteredMappings);
    };

    const onSelectMultipleSource = (fromName, fromX, fromY, toId, toName, isChild, isGlobal, globalValuePosition, targetFieldTypeId) => {
        var index = $importTemplatesMappingItems.value.length + 1;
        $importTemplatesMappingItems.set([...$importTemplatesMappingItems.value,
        { from: { name: fromName, x: fromX, y: fromY, isGlobal: isGlobal, globalValuePosition: globalValuePosition, formatting: null }, to: { id: toId, name: toName, targetFieldTypeId: targetFieldTypeId }, isChild: isChild, index: index }]);
    }

    const onDeleteStatic = (fieldName: string, value: string, id: number) => {
        var filteredStaticFields = $importTemplatesStaticFieldItems.value.filter((item) => !(item.fieldName == fieldName && item.value == value && item.fieldId == id));
        $importTemplatesStaticFieldItems.set(filteredStaticFields);
    };

    const onDeleteComputed = (fieldId1: number, fieldId2: number, resultFieldId: number) => {
        var filteredComputedFields = $importTemplatesComputedFieldItems.value.filter((item) => !(item.field1.id == fieldId1 && item.field2.id == fieldId2 && item.resultField.id == resultFieldId));
        $importTemplatesComputedFieldItems.set(filteredComputedFields);
    };

    const mergeMappings = (fromName, fromX, fromY, toId, toName, isChild, isGlobal, globalValuePosition, targetFieldTypeId, index) => {
        const mappings = $importTemplatesMappingItems.value.some((item) => item.index == index)
            ? $importTemplatesMappingItems.value.map((item) => item.index == index
                ?
                { from: { name: item.from.name, x: item.from.x, y: item.from.y, isGlobal: isGlobal, globalValuePosition: globalValuePosition, formatting: null }, to: { id: toId, name: toName, targetFieldTypeId: targetFieldTypeId }, isChild: isChild, index: index } as any
                :
                item
            )
            : [...$importTemplatesMappingItems.value,
            { from: { name: fromName, x: fromX, y: fromY, isGlobal: isGlobal, globalValuePosition: globalValuePosition, formatting: null }, to: { id: toId, name: toName, targetFieldTypeId: targetFieldTypeId }, isChild: isChild, index: index }];
        return mappings;
    };

    const mergeStaticFields = (fieldName, value, id: number) => {
        const staticFields = $importTemplatesStaticFieldItems.value.some((item) => item.fieldId == id)
            ? $importTemplatesStaticFieldItems.value.map((item) => item.fieldId == id
                ? { fieldName: fieldName, value: item.value, fieldId: item.fieldId }
                : item)
            : [...$importTemplatesStaticFieldItems.value, { fieldName: fieldName, value: value, fieldId: id }];
        return staticFields;
    };

    const mergeComputedFields = (field1, field2, operator, resultField) => {
        // const computedFields = $importTemplatesComputedFieldItems.value.some((item) => item.field1.id == field1.id)
        //     ? $importTemplatesComputedFieldItems.value.map((item) => item.field1.id == field1.id
        //         ? {
        //             field1: { id: item.field1.id, name: item.field1.name },
        //             field2: { id: item.field2.id, name: item.field2.name },
        //             operator: item.operator,
        //             resultField: { id: item.resultField.id, name: item.resultField.name }
        //         }
        //         : item)
        //     : [...$importTemplatesComputedFieldItems.value, {
        //         field1: { id: field1.id, name: field1.name },
        //         field2: { id: field2.id, name: field2.name },
        //         operator: operator,
        //         resultField: { id: resultField.id, name: resultField.name }
        //     }];
        // return computedFields;
        return [...$importTemplatesComputedFieldItems.value, {
            field1: { id: field1.id, name: field1.name },
            field2: { id: field2.id, name: field2.name },
            operator: operator,
            resultField: { id: resultField.id, name: resultField.name }
        }];
    };

    const onSelect = (fromName, fromX, fromY, toId, toName, isChild, isGlobal, globalValuePosition, targetFieldTypeId, index) => {
        //when single header is selected for mapping
        var indexNew = index ? index : $importTemplatesMappingItems.value.length + 1;
        $importTemplatesMappingItems.set(mergeMappings(fromName, fromX, fromY, toId, toName, isChild, isGlobal, globalValuePosition, targetFieldTypeId, indexNew));
    };

    const onSelectStatic = (fieldName, value, id) => {
        $importTemplatesStaticFieldItems.set(mergeStaticFields(fieldName, value, id));
    };

    const onSelectComputed = (field1, field2, operator, resultField) => {
        $importTemplatesComputedFieldItems.set(mergeComputedFields(field1, field2, operator, resultField));
    };

    const onSelections = (items: { fromName: string, fromX: number, fromY: number, toId: string, toName: string, isGlobal: boolean }[]) => {
        //when multiple headers are selected for mapping
        const mappingItemsLength = $importTemplatesMappingItems.value.length ? $importTemplatesMappingItems.value.length : 1;
        const mappings = items
            .filter((item) => $importTemplatesMappingItems.value.every((existingItem) => !(existingItem.from.x == item.fromX && existingItem.from.y == item.fromY)))
            .map((item, index) => ({ from: { name: item.fromName, x: item.fromX, y: item.fromY, isGlobal: item.isGlobal, formatting: null }, to: { id: item.toId, name: item.toName }, isChild: false, index: mappingItemsLength + index + 1 })) as MappingItem[];

        $importTemplatesMappingItems.set([...$importTemplatesMappingItems.value, ...mappings]);
    };

    const onClose = () => {
        $importTemplatesMappingItems.set([]);
    };

    const onCheck = (fromX, fromY, isGlobal, globalValuePosition) => {
        const mappings = $importTemplatesMappingItems.value.map((item) => item.from.x == fromX && item.from.y == fromY
            ? { ...item, from: { name: item.from.name, x: item.from.x, y: item.from.y, isGlobal: isGlobal, globalValuePosition: globalValuePosition, formatting: item.from.formatting } }
            : item);

        $importTemplatesMappingItems.set(mappings);
    };

    const onFormattingSelect = (fromX, fromY, formatting, targetFieldTypeId) => {
        const mappings = $importTemplatesMappingItems.value.map((item) => item.from.x == fromX && item.from.y == fromY
            ? { ...item, from: { name: item.from.name, x: item.from.x, y: item.from.y, isGlobal: item.from.isGlobal, globalValuePosition: item.from.globalValuePosition, formatting: formatting as Formatting }, to: { targetFieldTypeId: targetFieldTypeId, id: item.to.id, name: item.to.name } }
            : item);

        $importTemplatesMappingItems.set(mappings);
    };

    const rebuildMappingItems = (callBack?: any): MappingItem[] => {
        return $importTemplatesMappingItems.value.length > 0 ?
            $importTemplatesMappingItems.value.map((item) => ({
                ...item,
                from: {
                    name: callBack ? callBack(item.from.y, item.from.x) : item.from.name,
                    x: item.from.x,
                    y: item.from.y,
                    isGlobal: item.from.isGlobal,
                    globalValuePosition: item.from.globalValuePosition,
                    formatting: item.from.formatting
                }
            })) :
            $importTemplatesMappingItems.value;
    };

    const rebuildInBlockMappingFields = () => {
        return orderBy(state.inBlockMappingFields[currentInBlockMappingFieldsId()].mappingField.filter((inBlockMappingField) =>
            $importTemplatesMappingItems.value.every((mapping) => mapping.to.id !== inBlockMappingField.id) &&
            $importTemplatesStaticFieldItems.value.every((staticField) => staticField.fieldName !== inBlockMappingField.name) &&
            inBlockMappingField.id !== importTemplate.selectOption.id), [imp => imp.name.toLowerCase()], ['asc']);
    };

    const rebuildInBlockComputedFields = () => {
        return orderBy(state.inBlockMappingFields[currentInBlockMappingFieldsId()].mappingField.filter((inBlockMappingField) =>
            $importTemplatesMappingItems.value.some((mapping) => mapping.to.id === inBlockMappingField.id) &&
            //(!$importTemplatesComputedFieldItems.value.length || $importTemplatesComputedFieldItems.value.some((computedField) => computedField.field1.name !== inBlockMappingField.name)) &&
            inBlockMappingField.id !== importTemplate.selectOption.id), [imp => imp.name.toLowerCase()], ['asc']);
    }

    const changeSelectColumnMode = () => {
        $selectColumnMode.set(!$selectColumnMode.value);
    }

    const setSelectedColumn = (filterColumn) => {
        handleState({
            assetFilterCriteria: {
                filterColumn: filterColumn,
                criteria: importTemplateDetails.assetTypes.map(assetType => {
                    return { assetType: assetType, searchValue: "" }
                })
            }
        });
    }

    const deleteColumnFilter = () => {
        handleState({ assetFilterCriteria: {} as AssetFilterCriteria });
        $selectColumnMode.set(false);
    }

    const filterMappingItems = (mappings: Mapping[]) => {
        return mappings//.filter(i => i.mappingItems.length !== 0)
            .map(i => ({
                assetTypeId: i.assetTypeId, validationRuleId: i.validationRuleId, mappingItems: i.mappingItems
                    .map(el => ({ ...el, to: { ...el.to, targetFieldTypeId: el.to.targetFieldTypeId ? el.to.targetFieldTypeId : 1 } }))
            }));
    }

    const handleCloseShowErrors = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        $showErrors.set(false);
        $errorMessage.set("Error happened!");
    };

    const save = async () => {
        await importTemplateCreate.post({
            file: importTemplateDetails.file,
            name: importTemplateDetails.name,
            assetTypes: importTemplateDetails.assetTypes,
            isSingleAsset: importTemplateDetails.isSingleAsset,
            isDefault: importTemplateDetails.isDefault,
            metadata: importTemplateDetails.metadata,
            mappings: filterMappingItems(importTemplateDetails.mappings),
            staticFields: [...importTemplateDetails.staticFields.filter(i => i.staticFieldItems.length !== 0)],
            computedFields: [...importTemplateDetails.computedFields.filter(i => i.computedFieldItems.length !== 0)],
            companies: importTemplateDetails.companies.map((item) => ({ id: item.id, name: item.name })),
            omitUnmappedFields: $omitUnmappedFields.value,
            delimiter: importTemplateDetails.delimiter,
            assetFilterCriteria: importTemplateDetails.assetFilterCriteria,
            workSheet: importTemplateDetails.workSheet
        })
            .then(() => (navigate('import-templates')))
            .catch(err => {
                $showErrors.set(true);
                $errorMessage.set(err.message);
                $disableSave.set(false);
                return;
            });
    };

    const update = async () => {
        await importTemplateUpdate.put(getImportTemplateDetails())
            .then(() => { goToStep(0); showSuccessfulEditMessage.set(true) })
            .catch(err => {
                $showErrors.set(true);
                $errorMessage.set(err.message);
                $disableSave.set(false);
                return;
            });
    };

    const getImportTemplateDetails = () => {
        return {
            id: importTemplateDetails.id,
            name: importTemplateDetails.name,
            assetTypes: importTemplateDetails.assetTypes,
            isSingleAsset: importTemplateDetails.isSingleAsset,
            isDefault: importTemplateDetails.isDefault,
            isActive: importTemplateDetails.isActive,
            metadata: importTemplateDetails.metadata,
            mappings: filterMappingItems(importTemplateDetails.mappings),
            staticFields: [...importTemplateDetails.staticFields.filter(i => i.staticFieldItems.length !== 0)],
            computedFields: [...importTemplateDetails.computedFields.filter(i => i.computedFieldItems.length !== 0)],
            companies: importTemplateDetails.companies.map((item) => ({ id: item.id, name: item.name })),
            omitUnmappedFields: $omitUnmappedFields.value,
            delimiter: importTemplateDetails.delimiter,
            assetFilterCriteria: importTemplateDetails.assetFilterCriteria,
            workSheet: importTemplateDetails.workSheet
        }
    }

    const $selectedTemplateTab = useLinked('1');
    const $validationSets = useLinked({ id: '0', name: '' }).onChange(async x => {
        await validationRuleDetails.get(x.id).then();
        if (importTemplateDetails.mappings.length) {
            if (importTemplateDetails.mappings.some(i => i.assetTypeId == +$currentAssetType.value.id)) {
                importTemplateDetails.mappings.find(i => i.assetTypeId == +$currentAssetType.value.id).validationRuleId = +x.id;
            } else {
                importTemplateDetails.mappings = [...importTemplateDetails.mappings, { assetTypeId: +$currentAssetType.value.id, validationRuleId: +x.id, mappingItems: [] }]
            }
        } else {
            importTemplateDetails.mappings = [{ assetTypeId: +$currentAssetType.value.id, validationRuleId: +x.id, mappingItems: [] }]
        }
    });

    const getValidationSetItems = React.useCallback(async (searchText) => {
        return (await getValidationSets(searchText, +$currentAssetType.value.id));
    }, []);

    return (isReady ? <>
        <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={$showErrors.value} autoHideDuration={6000} onClose={handleCloseShowErrors}>
            <Alert severity="error">
                <AlertTitle>Error during saving</AlertTitle>
                {$errorMessage.value}
            </Alert>
        </Snackbar>
        <MapperTitleContainer>
            Step {isEdit ? 2 : 3}: View Parse Results, Map Fields and Save Import Template
        </MapperTitleContainer>
        <MapperBodyContainer>
            <FlexBody row>
                {isStandard ? <></> :
                    <FileReviewContainer>
                        <PlateHeader>
                            <Flex column className={classes.root}>
                                <span>{importTemplateDetails.originalFileName}</span>
                            </Flex>
                            <Flex column className={classes.root}>
                                {isEdit &&
                                    <FileDownloadButton
                                        text={'Download Original File'}
                                        file={importTemplateDetails.file}
                                        fileName={importTemplateDetails.originalFileName}
                                        icon='fa fa-download pointer' />

                                }
                            </Flex>
                            {$sheetList.value.length > 1 &&
                                <>
                                    <SheetLabel>Sheet:</SheetLabel>
                                    <DigiSelectContainer>
                                        <DigiSelect
                                            id="id"
                                            label="name"
                                            $value={$workSheet}
                                            options={$sheetList.value}
                                            styles={selectStyles} />
                                    </DigiSelectContainer>
                                </>
                            }
                        </PlateHeader>
                        <FileReaderView
                            csvData={state.data}
                            onSelect={onSelect}
                            onSelections={onSelections}
                            inBlockMappingFields={rebuildInBlockMappingFields()}
                            mappingItems={$importTemplatesMappingItems.value}
                            isSelectColumn={$selectColumnMode.value}
                            setSelectedColumn={setSelectedColumn}
                        />
                    </FileReviewContainer>
                }
                <FileMapperContainer>
                    <PlateHeader>
                        <Column>
                            <span>Map your source file fields</span>
                        </Column>
                    </PlateHeader>
                    <PlateContainer>
                        <TemplateTabs value={$selectedTemplateTab.value}
                            variant="scrollable"
                            scrollButtons="auto"
                            onChange={(evt, newValue) => $selectedTemplateTab.set(newValue)} >
                            <TemplateTab value="1" label="Mapping" />
                            {!isStandard && <TemplateTab value="2" label="Validations" />}
                        </TemplateTabs>
                        <TemplateTabContent>
                            {$selectedTemplateTab.value === '1' &&
                                <>
                                    {!$isLoading.value && (
                                        <FileMapperFields
                                            isStandard={isStandard}
                                            file={importTemplateDetails.file}
                                            omitUnmappedFields={$omitUnmappedFields}
                                            mappingItems={isStandard ? rebuildMappingItems() : rebuildMappingItems((fromY: number, fromX: number) => state.data[fromY - 1][fromX - 1])}
                                            staticFieldItems={$importTemplatesStaticFieldItems.value}
                                            computedFieldItems={$importTemplatesComputedFieldItems.value}
                                            inBlockMappingFields={orderBy(state.inBlockMappingFields[currentInBlockMappingFieldsId() >= 0 ? currentInBlockMappingFieldsId() : 0].mappingField, [imp => imp.name.toLowerCase()], ['asc'])}
                                            inBlockStaticFields={rebuildInBlockMappingFields()}
                                            inBlockComputedFields={rebuildInBlockComputedFields()}
                                            onSelect={onSelect}
                                            onSelectStatic={onSelectStatic}
                                            onSelectComputed={onSelectComputed}
                                            onDelete={onDelete}
                                            onDeleteStatic={onDeleteStatic}
                                            onDeleteComputed={onDeleteComputed}
                                            onCheck={onCheck}
                                            onClose={onClose}
                                            onSelectMultipleSource={onSelectMultipleSource}
                                            onFormattingSelect={onFormattingSelect}
                                            assetTypes={importTemplateDetails.assetTypes}
                                            $currentAssetType={$currentAssetType}
                                            selectColumnMode={changeSelectColumnMode}
                                            assetFilterCriteria={importTemplateDetails.assetFilterCriteria}
                                            deleteColumnFilter={deleteColumnFilter}
                                            workSheet={importTemplateDetails.workSheet}
                                        />
                                    )}
                                </>
                            }
                            {$selectedTemplateTab.value === '2' &&
                                <>
                                    <TemplateTitle>Asset Type: {$currentAssetType.value.name}</TemplateTitle>
                                    <TemplateTitle>Validation Set</TemplateTitle>
                                    <DigitizationSelect
                                        placeholder={'Validation Sets'}
                                        $value={$validationSets}
                                        getItems={getValidationSetItems}
                                        isMulti={false}
                                    />
                                    {$validationSets.value.name &&
                                        <>
                                            {validationRuleDetails.id ?
                                                <MainContainer>
                                                    <PlateHeader>
                                                        <Column>
                                                            <span>Validation Fields</span>
                                                        </Column>
                                                    </PlateHeader>
                                                    <PlateContainer>
                                                        <Flex row>
                                                            <RequiredFieldColumn>Required</RequiredFieldColumn>
                                                            <FieldNameColumn>Name</FieldNameColumn>
                                                        </Flex>
                                                        {
                                                            validationRuleDetails.ruleSets.map((item, index) => {
                                                                return (
                                                                    <FieldRow
                                                                        data-qa-id={`inBlockTargetFields${index}`}>
                                                                        <CheckboxCol>
                                                                            <DigiCheckbox
                                                                                checked={item.isRequired}
                                                                                readOnly={true} />
                                                                        </CheckboxCol>
                                                                        <FieldNameColumn>
                                                                            {item.fieldName}
                                                                        </FieldNameColumn>
                                                                    </FieldRow>
                                                                )
                                                            })
                                                        }
                                                    </PlateContainer>
                                                </MainContainer>
                                                : <Loading />
                                            }
                                        </>
                                    }
                                </>
                            }
                        </TemplateTabContent>

                    </PlateContainer>
                </FileMapperContainer>
            </FlexBody>
            <Flex row stretch>
                <ExpandedModelContainer id="expandContainer" ></ExpandedModelContainer>
            </Flex>
        </MapperBodyContainer>
        <WorkflowFooter>
            <Columns>
                <Column>
                    <Flex row>
                        <CancelButton color="primary" onClick={cancel}>Cancel</CancelButton>
                        {isEdit ? <></> : <StartOverButton color="primary" onClick={startOver}>Start Over</StartOverButton>}
                    </Flex>
                </Column>
                <Column>
                    <RightAlignedButtons>
                        <BackButton color="primary" onClick={back}>Back</BackButton>
                        {isStandard ? <></> :
                            <ConfirmationButton
                                disabled={$disableSave.value}
                                onClick={() => {
                                    $disableSave.set(true);
                                    importTemplateDetails.id ? update() : save()
                                }}
                            >Next: Save Import Template</ConfirmationButton>
                        }
                    </RightAlignedButtons>
                </Column>
            </Columns>
        </WorkflowFooter>
    </> : <Loading />
    );
};

const SheetLabel = span({
    marginLeft: 'auto',
    marginRight: '10px'
});

const DigiSelectContainer = div({
    minWidth: '200px',
    height: '35px',
    marginRight: '16px'
});

const MapperBodyContainer = div({
    display: 'flex',
    marginTop: '15px',
    justifyContent: 'center',
    width: '100%',
    flexDirection: 'column',
    alignItems: 'stretch',
    minHeight: 'calc(100% - 220px)',
    maxHeight: 'calc(100% - 220px)',
    paddingBottom: '25px',
    overflow: 'auto'
});

const MapperTitleContainer = div({
    color: '#3d6889',
    overflow: 'hidden',
    fontSize: '16px',
    marginTop: '10px',
    fontFamily: 'Montserrat',
    fontWeight: 600,
    marginLeft: '5px',
    whiteSpace: 'nowrap',
    textTransform: 'capitalize'
});

const FileMapperContainer = div({
    marginLeft: '10px',
    flex: '0 0 550px',
    overflow: 'auto',
    borderLeft: '1px solid white',
    background: 'whitesmoke'
});

const FileReviewContainer = div({
    flex: '1 0 450px',
    overflow: 'auto',
    background: 'whitesmoke'
});

const PlateHeader = div({
    display: 'Flex',
    height: '50px',
    background: '#052C4C',
    width: 'auto',
    alignItems: 'center',
    color: 'white'
});

const PlateContainer = div({
    borderRadius: '4px',
    padding: '8px',
    justifyContent: 'center',
    maxHeight: 'calc(100% - 50px)',
    overflow: 'auto'
});

const ExpandedModelContainer = div({
    marginTop: '10px',
    width: '100%'
});

const FlexBody = styled(Flex)({
    overflow: 'auto',
    flex: '1 1 auto'
})

const useTextStyles = makeStyles({
    root: {
        marginLeft: '1em',
        marginRight: '1em'
    }
})

const selectStyles = {
    control: provided => ({
        ...provided,
        minHeight: 35,
    })
}

const TemplateTabs = withStyles({
    indicator: {
        display: 'none'
    },
    root: {
        marginTop: '8px'
    }
})((props: any) => <Tabs {...props} TabIndicatorProps={{ children: <span /> }} />);

const TemplateTab = withStyles({
    root: {
        minWidth: '73px',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
        padding: '3px 10px 6px',
        position: 'static',
        height: '26px',
        left: '46.58px',
        background: (props: any) => props.selected ? '#DCEEFD' : 'white',
        fontWeight: (props: any) => props.selected ? 600 : 400,
        borderRadius: '4px',
        flex: 'none',
        margin: '0px 20px',
        textTransform: 'none'
    },
})((props: any) => <Tab disableRipple {...props} />);

const TemplateTabContent = div({
    border: 'none',
    marginTop: '10px',
    fontSize: '13px',
    fontWeight: 500,
    lineHeight: '15px',
    // overflow: 'auto',
    height: 'calc(100vh - 410px)'
});

const TemplateTitle = div({
    color: '#3d6889',
    overflow: 'hidden',
    fontSize: '16px',
    marginTop: '15px',
    marginBottom: '15px',
    fontFamily: 'Montserrat',
    fontWeight: 600,
    marginLeft: '5px',
    whiteSpace: 'nowrap',
});

const MainContainer = styled(Column)({
    flex: '1 0 450px',
    // overflow: 'auto',
    borderLeft: '1px solid whitesmoke',
    background: 'white',
    margin: '15px 0'
});

const BaseColumn = styled(Column)({
    color: '#668AA5',
    fontSize: '14px',
    fontWeight: 600
})

const RequiredFieldColumn = styled(BaseColumn)({
    marginLeft: '2em',
    flex: '0.2',
    justifyContent: 'center'
})

const CheckboxCol = styled(BaseColumn)({
    marginLeft: '10px',
    flex: '0.0',
    justifyContent: 'center'
})

const FieldNameColumn = styled(BaseColumn)({
    //flex: '0.5'
})

const FieldRow = div({
    display: 'flex',
    flexDirection: 'row',
    backgroundColor: 'whitesmoke',
    marginTop: 5,
    marginBottom: 3,
    borderRadius: 5,
    padding: '10px 10px',
    fontSize: '12px',
    fontFamily: 'Montserrat',
    fontWeight: 500,
});